import { useState } from 'react';
import { DatePicker, Modal, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { LangText } from '@/components';
import { preFixImgUrl } from '@/utils';
import { useNavigate } from 'react-router-dom';
import aiBtnImg from '@/assets/svg/ai-btn.svg';
import arrowRightIconImg from '@/assets/svg/arrow-right.svg';
import daysIconImg from '@/assets/svg/days-icon.svg';
import peopleIconImg from '@/assets/svg/people-icon.svg';
import departureIconImg from '@/assets/svg/departure-icon.svg';
import aiIconImg from '@/assets/svg/ai-icon.svg';

const AI_TRIP_BG = preFixImgUrl('9127bb7c98ad9a537d46b88d4e56409b.png');

const LEFT_SCROLL_IMAGES = [
  preFixImgUrl('83cc69225c79cff60df829144bb6bd8f.png'),
  preFixImgUrl('fed2ff9f62de05783791a89231978218.png'),
  preFixImgUrl('a3d6e436c6050097a5a2e57c2453d8bf.png'),
] as const;

const RIGHT_SCROLL_IMAGES = [
  preFixImgUrl('bf19676bc2b7cf47206273e32554c69b.png'),
  preFixImgUrl('f345f31d6d175c65837d1bcf1657dcc9.png'),
  preFixImgUrl('a8843d7b944b76aa89910731c33b84b9.png'),
] as const;

const RECOMMEND_TAGS = [
  { labelKey: 'home.aiTrip.recommend.familyTokyo', icon: preFixImgUrl('42d16a126bf44db07b54163497a92ddb.png') },
  { labelKey: 'home.aiTrip.recommend.kansaiDeep', icon: preFixImgUrl('5d99c47435a7be3ed43ed3ab1564c358.png') },
  { labelKey: 'home.aiTrip.recommend.shopping', icon: preFixImgUrl('90d6505acb10f58c8e29cf0a2dda8100.png') },
] as const;

const SEARCH_ITEMS = [
  {
    id: 'date',
    labelKey: 'common.form.departureDate',
    valueKey: 'home.aiTrip.search.departurePlaceholder',
    icon: <img src={departureIconImg} alt="" className="h-[28px] w-[28px]" />,
  },
  {
    id: 'days',
    labelKey: 'common.form.days',
    valueKey: 'home.aiTrip.search.daysPlaceholder',
    icon: <img src={daysIconImg} alt="" className="h-[24px] w-[24px]" />,
  },
  {
    id: 'people',
    labelKey: 'common.form.people',
    valueKey: 'home.aiTrip.search.peoplePlaceholder',
    icon: <img src={peopleIconImg} alt="" className="h-[24px] w-[24px]" />,
  },
] as const;

const SectionTitle = () => {
  return (
    <div className="flex flex-col items-center">
      <LangText lngKey="home.aiTrip.title" className="text-[36px] font-bold leading-[50px] text-[#000]" />
      <span className="mt-[16px] h-[4px] w-[108px] rounded-full bg-primary" />
    </div>
  );
};

const TripSearchCard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [departureDate, setDepartureDate] = useState<Dayjs | null>(null);
  const [days, setDays] = useState<number>();
  const [people, setPeople] = useState<number>();
  const [activePicker, setActivePicker] = useState<'date' | 'days' | 'people' | null>(null);

  const dayOptions = Array.from({ length: 30 }, (_, index) => {
    const value = index + 1;
    return {
      value,
      label: t('home.tripSearch.daysValue', { count: value }),
    };
  });

  const peopleOptions = Array.from({ length: 10 }, (_, index) => {
    const value = index + 1;
    return {
      value,
      label: t('home.tripSearch.peopleValue', { count: value }),
    };
  });

  const generateTrip = () => {
    const searchParams = new URLSearchParams();

    if (departureDate) {
      searchParams.set('departureDate', departureDate.format('YYYY-MM-DD'));
    }
    if (days) {
      searchParams.set('days', String(days));
    }
    if (people) {
      searchParams.set('people', String(people));
    }

    const query = searchParams.toString();
    navigate(`/ai-trip${query ? `?${query}` : ''}`);
  };

  return (
    <div className="flex h-[68px] w-full items-center rounded-full bg-white pl-[14px] pr-[10px]">
      <div className="flex flex-1 items-center">
        {SEARCH_ITEMS.map((item) => (
          <div
            key={item.labelKey}
            className="flex flex-1 cursor-pointer items-center gap-[12px]"
            onClick={() => setActivePicker(item.id)}
          >
            <span className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-[#F4F4F4]">
              {item.icon}
            </span>
            <div className="flex flex-col whitespace-nowrap">
              <LangText lngKey={item.labelKey} className="text-[14px] font-bold leading-[20px] text-[#000]" />
              {item.id === 'date' && departureDate ? (
                <span className="mt-[4px] text-[12px] leading-[17px] text-[#555]">
                  {departureDate.format('YYYY-MM-DD')}
                </span>
              ) : item.id === 'days' && days ? (
                <span className="mt-[4px] text-[12px] leading-[17px] text-[#555]">
                  {t('home.tripSearch.daysValue', { count: days })}
                </span>
              ) : item.id === 'people' && people ? (
                <span className="mt-[4px] text-[12px] leading-[17px] text-[#555]">
                  {t('home.tripSearch.peopleValue', { count: people })}
                </span>
              ) : (
                <LangText lngKey={item.valueKey} className="mt-[4px] text-[12px] leading-[17px] text-[#979797]" />
              )}
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={generateTrip}
        className="flex h-[50px] shrink-0 items-center rounded-full bg-primary pl-[18px] pr-[20px] text-[18px] font-bold text-white transition-opacity hover:opacity-90"
      >
        <img src={aiBtnImg} alt="" className="h-[16px] w-[24px] shrink-0 object-contain mb-[3px]" />
        <LangText lngKey="home.common.generateTrip" className="ml-[4px] mr-[8px] text-[18px]" />
        <img src={arrowRightIconImg} alt="" className="h-[16px] w-[16px] shrink-0 object-contain" />
      </button>

      <Modal
        open={activePicker !== null}
        onCancel={() => setActivePicker(null)}
        footer={null}
        centered
        destroyOnHidden
        width={activePicker === 'date' ? 440 : 520}
        title={activePicker ? (
          <LangText
            lngKey={
              activePicker === 'date'
                ? 'common.form.departureDate'
                : activePicker === 'days'
                  ? 'common.form.days'
                  : 'common.form.people'
            }
            className="text-[20px] font-bold text-[#000]"
          />
        ) : null}
      >
        {activePicker === 'date' ? (
          <DatePicker
            value={departureDate}
            disabledDate={(current) => current.isBefore(dayjs().startOf('day'))}
            onChange={(value) => {
              setDepartureDate(value);
              setActivePicker(null);
            }}
            format="YYYY-MM-DD"
            placeholder={t('home.tripSearch.selectDate')}
            allowClear={false}
            inputReadOnly
            className="home-trip-modal-control h-[44px] w-full"
          />
        ) : null}

        {activePicker === 'days' ? (
          <Select<number>
            value={days}
            onChange={(value) => {
              setDays(value);
              setActivePicker(null);
            }}
            options={dayOptions}
            placeholder={t('common.form.pleaseSelect')}
            classNames={{ popup: { root: 'home-trip-modal-dropdown' } }}
            styles={{ placeholder: { fontSize: 16, lineHeight: '24px' } }}
            className="home-trip-modal-control h-[44px] w-full"
          />
        ) : null}

        {activePicker === 'people' ? (
          <Select<number>
            value={people}
            onChange={(value) => {
              setPeople(value);
              setActivePicker(null);
            }}
            options={peopleOptions}
            placeholder={t('home.tripSearch.selectPeople')}
            classNames={{ popup: { root: 'home-trip-modal-dropdown' } }}
            styles={{ placeholder: { fontSize: 16, lineHeight: '24px' } }}
            className="home-trip-modal-control h-[44px] w-full"
          />
        ) : null}
      </Modal>
    </div>
  );
};

const ImageRail = ({
  images,
  direction,
  className,
}: {
  images: readonly string[];
  direction: 'up' | 'down';
  className: string;
}) => {
  return (
    <div className={`overflow-hidden absolute ${className}`}>
      <div
        className="flex flex-col gap-[12px]"
        style={{
          animation: `${direction === 'up' ? 'ai-trip-scroll-up' : 'ai-trip-scroll-down'} 18s linear infinite`,
        }}
      >
        {[...images, ...images].map((image, index) => (
          <img
            key={`${image}-${index}`}
            src={image}
            alt=""
            className="h-[265px] w-[234px] shrink-0 rounded-[26px] object-cover"
            draggable={false}
          />
        ))}
      </div>
    </div>
  );
};

export const AiTripPlanner = () => {
  return (
    <section className="mx-auto w-[1200px] pt-[82px]">
      <style>
        {`
          @keyframes ai-trip-scroll-up {
            from { transform: translateY(0); }
            to { transform: translateY(calc(-50% - 9px)); }
          }
          @keyframes ai-trip-scroll-down {
            from { transform: translateY(calc(-50% - 9px)); }
            to { transform: translateY(0); }
          }
        `}
      </style>
      <SectionTitle />
      <div
        className="relative mt-[82px] h-[390px] overflow-hidden rounded-[20px] bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${AI_TRIP_BG})` }}
      >
        <div className="relative z-10 flex h-full gap-[40px]">
          <div className="min-w-0 flex-1 pl-[42px] pt-[60px]">
            <div className="flex items-center gap-[14px]">
              {RECOMMEND_TAGS.map((tag) => (
                <div
                  key={tag.labelKey}
                  className="flex h-[32px] items-center rounded-full border border-white/50 bg-white/31 pl-[4px] pr-[12px] text-white"
                >

                  <img src={tag.icon} alt="" className="h-[24px] w-[24px] mr-[8px]" />
                  <LangText lngKey={tag.labelKey} className="text-[14px] text-white" />
                </div>
              ))}
            </div>
            <div className="mt-[26px] flex items-center">
              <img src={aiIconImg} alt="" className="h-[32px] w-[32px] mr-[6px] text-white" />
              <LangText lngKey="home.aiTrip.title" className="text-[30px] font-bold leading-[42px] text-white" />
            </div>
            <LangText
              lngKey="home.aiTrip.desc"
              className="mt-[20px] block text-[20px] leading-[30px] text-white"
            />
            <div className="mt-[26px]">
              <TripSearchCard />
            </div>
          </div>
          <div className="relative h-full w-[418px] shrink-0">
            <ImageRail images={LEFT_SCROLL_IMAGES} direction="down" className="left-0 top-[-80px] h-[560px] w-[265px]" />
            <ImageRail images={RIGHT_SCROLL_IMAGES} direction="up" className="left-[248px] top-[-36px] h-[490px] w-[234px]" />
          </div>
        </div>
      </div>
    </section>
  );
};
